import { inject, Injectable } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { EditablePopupGeojsonComponent } from '@components/editable-popup-geojson/editable-popup-geojson.component'
import { POP_UP_DOM_ELEMENT_NAME } from '@core/constants/constant.data'
import {
  AppMarkSymbolizer,
  FeaturesByCoord,
  GeoJSONFeatureWithSymbolizer,
  Geometry,
} from '@core/models/geojson.model'
import * as L from 'leaflet'
import { SLDService } from './sld.service'

@Injectable({
  providedIn: 'root',
})
export class GeojsonService {
  private readonly _sldService = inject(SLDService)

  // Public API
  buildGeoJSONLayerGroup(geoJson: any): L.LayerGroup {
    return this._buildGeoJSONLayerGroupInternal(geoJson).layerGroup
  }

  buildNonClickableGeoJSONLayerGroup(geoJson: any): L.LayerGroup {
    return this._buildGeoJSONLayerGroupInternal(geoJson, { interactive: false }).layerGroup
  }

  async buildStylizedGeoJsonLayerGroup(geoJson: any, sldXml: any): Promise<any> {
    const style = await this._sldService.parseSLD(sldXml)
    if (!style?.rules?.length) {
      console.error('Failed to parse SLD style, display geojson with default option')
      return this.buildGeoJSONLayerGroup(geoJson)
    }
    const geoJsonWithStyle = this._sldService.applyStyleToGeoJSONFeatures(geoJson, style)
    return this.buildGeoJSONLayerGroup(geoJsonWithStyle)
  }

  buildEditableGeoJSONLayerGroup(geoJson: L.GeoJSON): {
    layerGroup: L.LayerGroup
    featureLayerMap?: Map<number, L.Layer>
  } {
    return this._buildGeoJSONLayerGroupInternal(geoJson, {
      editablePopup: true,
      returnFeatureLayerMap: true,
    })
  }

  async buildStylizedEditableGeoJsonLayerGroup(
    geoJson: any,
    sldXml: any,
  ): Promise<{
    layerGroup: L.LayerGroup
    featureLayerMap?: Map<number, L.Layer>
  }> {
    const style = await this._sldService.parseSLD(sldXml)
    if (!style) {
      return geoJson
    }
    const geoJsonWithStyle = this._sldService.applyStyleToGeoJSONFeatures(geoJson, style)
    return this._buildGeoJSONLayerGroupInternal(geoJsonWithStyle, {
      editablePopup: true,
      returnFeatureLayerMap: true,
    })
  }

  // groups all common logic for different case of building geojson layers
  private _buildGeoJSONLayerGroupInternal(
    geoJson: any,
    {
      editablePopup = false,
      returnFeatureLayerMap = false,
      interactive = true,
    }: { editablePopup?: boolean; returnFeatureLayerMap?: boolean; interactive?: boolean } = {},
  ): { layerGroup: L.LayerGroup; featureLayerMap?: Map<number, L.Layer> } {
    const layerGroup = new L.LayerGroup()
    const featureLayerMap = returnFeatureLayerMap ? new Map<number, L.Layer>() : undefined
    const extraOptions: L.GeoJSONOptions = interactive ? {} : { interactive: false }

    // Group features by coordinates for popups (skipped for non-interactive layers)
    const featuresByCoord = new Map<string, any[]>()
    if (interactive) {
      geoJson.features.forEach((feature: GeoJSONFeatureWithSymbolizer, idx: number) => {
        const coordKey = JSON.stringify(feature.geometry.coordinates)
        if (!featuresByCoord.has(coordKey)) {
          featuresByCoord.set(coordKey, [])
        }
        featuresByCoord.get(coordKey)?.push({
          id: feature.id,
          properties: feature.properties,
          __geojsonIndex: idx,
        })
      })
    }

    geoJson.features.forEach((feature: GeoJSONFeatureWithSymbolizer, idx: number) => {
      const geom = feature.geometry
      if (geom === null) {
        console.error('Geom is null')
        return
      }

      const layer = this._constructLayerFromFeature(feature, geom, extraOptions)
      if (featureLayerMap) featureLayerMap.set(idx, layer)

      if (interactive) {
        layer.bindPopup(() => this._constructPopup(featuresByCoord, feature, editablePopup))
      }
      layerGroup.addLayer(layer)
    })

    return returnFeatureLayerMap ? { layerGroup, featureLayerMap } : { layerGroup }
  }

  private _constructPopup(
    featuresByCoord: FeaturesByCoord,
    feature: GeoJSONFeatureWithSymbolizer,
    isEditable: boolean,
  ) {
    const popup: NgElement & WithProperties<EditablePopupGeojsonComponent> = document.createElement(
      POP_UP_DOM_ELEMENT_NAME,
    ) as any

    // Passing the @Input s
    popup.features = featuresByCoord.get(JSON.stringify(feature.geometry.coordinates)) ?? []
    popup.isEditable = isEditable

    return popup
  }

  //@TODO: check if there is a difference between Point/MultiPoint LineString/MultiLineString Polygon/MultiPolygon
  _constructLayerFromFeature(
    feature: GeoJSONFeatureWithSymbolizer,
    geom: Geometry,
    extraOptions: L.GeoJSONOptions = {},
  ): L.Layer {
    let layer: L.Layer
    if (
      geom.type === 'LineString' ||
      geom.type === 'MultiLineString' ||
      geom.type === 'Polygon' ||
      geom.type === 'MultiPolygon'
    ) {
      layer = L.geoJSON(feature, {
        style: this._getPathOptionsFromFeature(feature),
        ...extraOptions,
      })

      // Handling all layer with "point" on map (icon, point,...)
    } else if (geom.type === 'Point' || geom.type === 'MultiPoint') {
      layer = this._constructPointFromFeature(feature, extraOptions)
    } else {
      console.warn('This type of geom not supported:', geom.type)
      return L.layerGroup()
    }
    return layer
  }

  // Use for line and polygon
  private _getPathOptionsFromFeature(feature: GeoJSONFeatureWithSymbolizer): L.PathOptions {
    const defaultFillColor = 'white'
    const defaultFillOpacity = 0.8
    const defaultColor = 'blue'
    const defaultWeight = 3

    const buildStyle = (fillColor: any, color: any, s: any): L.PathOptions => {
      const style: L.PathOptions = {
        fillColor: fillColor ?? defaultFillColor,
        fillOpacity: s.fillOpacity ?? defaultFillOpacity,
        color: color ?? defaultColor,
        weight: s.weight ?? defaultWeight,
        opacity: s.opacity ?? (color ? 1 : 0),
      }
      if (s.dashArray !== undefined) style.dashArray = s.dashArray
      return style
    }

    if (feature.originalSymbolizer) {
      return buildStyle(feature.style.fillColor, feature.style.color, feature.style)
    }

    if (feature.style?.fill) {
      // Personal layer (.csv, .geojson) with style written in geojson
      return buildStyle(feature.style.fill, feature.style.stroke, feature.style)
    }

    if (feature.style) {
      // OM4A EV-PV and openTepes: style keys passed through directly
      const styleEntries = Object.entries(feature.style).filter(
        ([, value]) => value !== undefined && value !== null,
      )
      const style = Object.fromEntries(styleEntries) as L.PathOptions
      return style
    }

    return {
      fillColor: defaultFillColor,
      fillOpacity: defaultFillOpacity,
      color: defaultColor,
      weight: defaultWeight,
      opacity: 1,
    }
  }

  private _constructPointFromFeature(
    feature: GeoJSONFeatureWithSymbolizer,
    extraOptions: L.GeoJSONOptions = {},
  ): L.Layer {
    const geojsonMarkerOptionsDefault = {
      radius: 4,
      fillColor: '#ffffff',
      fillOpacity: 1,
      color: '#000000',
      opacity: 1,
      weight: 1,
    }
    const layer = L.geoJSON(feature, {
      pointToLayer: (feature: GeoJSONFeatureWithSymbolizer, latlng: L.LatLng) => {
        if (feature.originalSymbolizer) {
          if (feature.originalSymbolizer.kind === 'Mark') {
            return this._constructMarkerFromSymbolizer(feature.originalSymbolizer, latlng)
          } else if (feature.originalSymbolizer.kind === 'Icon') {
            // Hardcoded case for excess_heat pie chart
            const svgHtml = this._constructPieChartFromFeature(feature)
            return this._svgMarker(svgHtml, 32, latlng)
          } else {
            console.warn('This type of kind not supported:', feature.originalSymbolizer.kind)
            return L.layerGroup()
          }
        }

        // Case for industrial layers in personal layer and custom geojson style
        //@todo, check if still needed after no-style implemenation of industrial layers
        if (feature.style) {
          if (feature.style.name) {
            return this._constructMarkerFromFeatureStyle(feature, latlng)
          }
          // Point without info are not shown
          return L.layerGroup()
        }

        return L.circleMarker(latlng, geojsonMarkerOptionsDefault)
      },
      ...extraOptions,
    })
    // parse again to check for pie chart or companyname
    layer.eachLayer((l: any) => {
      const feature = l.feature
      if (this._islayerCompanyname(feature.properties) && l instanceof L.CircleMarker) {
        l.bindTooltip(feature.properties.companyname, {
          permanent: true,
          direction: 'top',
        })
      }
    })
    return layer
  }

  private _islayerCompanyname(
    properties: unknown,
  ): properties is { companyname: unknown; srid: unknown } {
    if (typeof properties !== 'object' || properties === null) return false
    const keys = Object.keys(properties)
    return keys.length === 2 && keys.includes('companyname') && keys.includes('srid')
  }

  private _constructMarkerFromSymbolizer(symbolizer: AppMarkSymbolizer, latlng: L.LatLngLiteral) {
    let size = 2
    if (symbolizer.size) {
      size = symbolizer.size
    } else if (symbolizer.radius) {
      size = symbolizer.radius * 2
    }

    let shapeHTML = ''
    switch (symbolizer.wellKnownName.toLowerCase()) {
      case 'triangle':
        size = symbolizer.size ? symbolizer.size : 18
        const rotate = symbolizer.rotate ? symbolizer.rotate : 0
        shapeHTML = this._constructTriangleSVG(
          size,
          symbolizer.color,
          symbolizer.strokeColor,
          symbolizer.strokeWidth,
          rotate,
        )
        break

      case 'square':
        size = 12
        shapeHTML = this._constructSquareSVG(
          size,
          symbolizer.color,
          symbolizer.strokeColor,
          symbolizer.strokeWidth,
        )
        break

      case 'x':
        size = 12
        shapeHTML = `
          <svg width="${size}" height="${size}" viewBox="0 0 100 100">
            <rect width="130" height="10" style="fill: ${symbolizer.color}; stroke: rgb(0, 0, 0);" transform="rotate(-45)" y="65" x="-65"/>
            <rect width="130" height="10" style="fill: ${symbolizer.color}; stroke: rgb(0, 0, 0);" transform="rotate(45)" y="-5" x="5"/>
          </svg>`
        break

      // +
      case 'cross':
        size = 12
        shapeHTML = `
          <svg width="${size}" height="${size}" viewBox="0 0 100 100" transform="rotate(45)">
            <rect width="130" height="10" style="fill: ${symbolizer.color}; stroke: rgb(0, 0, 0);" transform="rotate(-45)" y="65" x="-65"/>
            <rect width="130" height="10" style="fill: ${symbolizer.color}; stroke: rgb(0, 0, 0);" transform="rotate(45)" y="-5" x="5"/>
          </svg>`
        break

      case 'star':
        size = 12
        shapeHTML = `
          <svg width="${size}" height="${size}" viewBox="0 0 100 100">
            <polygon points="50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35"
              fill="${symbolizer.color}" stroke="${symbolizer.strokeColor}" stroke-width="5"/>
          </svg>`
        break

      default:
        return L.circleMarker(latlng, {
          radius: size,
          fillColor: symbolizer.color ?? '#000000',
          fillOpacity: symbolizer.fillOpacity ?? 0.8,
          color: symbolizer.strokeColor ?? '#000000',
          weight: symbolizer.strokeWidth ?? 1,
          opacity: symbolizer.strokeOpacity ?? 1,
        })
    }

    const svgIcon: L.DivIcon = L.divIcon({
      html: shapeHTML,
      className: 'svg-icon',
      iconSize: [size, size],
      iconAnchor: [size / 2, size],
    })

    return L.marker(latlng, {
      icon: svgIcon,
    })
  }

  private _constructMarkerFromFeatureStyle(feature: any, latlng: L.LatLng) {
    if (feature.style.name == 'circle') {
      // circle marker
      const circleMaker = new L.CircleMarker(latlng, {
        fillColor: feature.style.fill,
        color: feature.style.stroke,
        fillOpacity: 1,
        weight: 1,
        // https://github.com/Leaflet/Leaflet/issues/2824
        radius: +feature.style.size / 2,
      })

      return circleMaker
    }

    if (feature.style.name == 'chart') {
      return this._svgMarker(this._constructPieChartSVG(feature.style), feature.style.size, latlng)
    }

    let svgHtml = ''
    switch (feature.style.name) {
      case 'triangle':
        svgHtml = this._constructTriangleSVG(
          feature.style.size,
          feature.style.fill,
          feature.style.stroke ?? 'black',
          feature.style.strokeWidth ?? 1,
          0,
        )
        break
      case 'square':
        svgHtml = this._constructSquareSVG(
          feature.style.size,
          feature.style.fill,
          feature.style.stroke,
        )
        break
      case 'custom':
        svgHtml = feature.style.customSVG
        break
      // @ToDo: pentagon, hexagon, octagon
    }
    return this._svgMarker(svgHtml, feature.style.size, latlng)
  }

  private _svgMarker(svgHtml: string, size: number, latlng: L.LatLng): L.Marker {
    return L.marker(latlng, {
      icon: L.divIcon({ html: svgHtml, className: 'svg-icon', iconSize: [size, size] }),
    })
  }

  private _constructSquareSVG(
    size: number,
    fill: string,
    strokeColor = '#000000',
    strokeWidth = 2,
  ): string {
    return `<svg height="${size}" width="${size}" xmlns="http://www.w3.org/2000/svg">
    <rect width="${size}" height="${size}" x="0" y="0" style="fill:${fill};stroke:${strokeColor};stroke-width:${strokeWidth}" />
    </svg>`
  }

  private _constructTriangleSVG(
    size: number,
    fill: string,
    strokeColor = '#000000',
    strokeWidth = 1,
    rotate: number,
  ): string {
    const halfWidth = size / 2
    const height = (Math.sqrt(3) / 2) * size

    return `<svg height="${height * 2}" width="${size * 2}" xmlns="http://www.w3.org/2000/svg" transform="rotate(${rotate})">
          <polygon points="0,${height} ${size},${height} ${halfWidth},0" style="fill:${fill};stroke:${strokeColor};stroke-width:${strokeWidth}" />
        </svg>`
  }

  private _constructPieChartSVG(chart: {
    data: { [key: string]: number }
    size: number
    chartOptions: { [key: string]: { fillColor: string } }
  }): string {
    const data = chart.data
    const chartOptions = chart.chartOptions as {
      [key: string]: { fillColor: string }
    }
    const size = chart.size / 3

    // calculating total sum of all values
    const total = Object.values(data).reduce((sum, value) => sum + value, 0)

    // generating base SVG element
    let svg = `<svg height="${size * 3}" width="${size * 3}" viewBox="0 0 ${size * 2.5} ${
      size * 2.5
    }">`

    // add offset grey circle, simulation of shadow
    svg += `<circle r="${size / 2}" cx="${size * 1.45}" cy="${size * 1.45}" fill="grey"
                  stroke="grey"
                  stroke-width="${size}"
      />`

    // add white background circle, simulation of border
    svg += `<circle r="${size / 2}" cx="${size * 1.25}" cy="${size * 1.25}" fill="white"
                  stroke="white"
                  stroke-width="${size + 2}"
      />`

    let startAngle = 0
    let endAngle = 0

    // constructing pie slices
    Object.keys(data).forEach((value) => {
      // Calculate the end angle of the current slice:
      // - adding the percentage of the current category to the previous end angle
      // - scale it to the range of 0 to 360 degrees
      const percentage = Math.round((data[value] / total) * 100)
      endAngle += (percentage / 100) * 360

      // Constructing a circle for each slice
      // - the *1.25 in cx & cy allows for the pie chart to be centered to its view box
      // each circle is a slice of pie that is add to the previous one
      // recuperates the end angle of the previous slice
      const color = chartOptions[value].fillColor
      svg += `<circle
                  r="${size / 2}"
                  cx="${size * 1.25}"
                  cy="${size * 1.25}"
                  fill="transparent"
                  stroke="${color}"
                  stroke-width="${size}"
                  stroke-dasharray="${(percentage / 100) * Math.PI * size},${Math.PI * size}"
                  stroke-dashoffset="${(-startAngle / 360) * Math.PI * size}"
              />`

      // Update the starting angle for the next slice
      startAngle = endAngle
    })

    // add the end html tag of the svg to close it once it iterated on each slice
    svg += '</svg>'

    return svg
  }

  // hardcoded logic for excess_heat pie chart
  private _constructPieChartFromFeature(feature: GeoJSONFeatureWithSymbolizer, size = 32): string {
    const properties = feature.properties

    type HeatKey = 'excess_heat_100_200c' | 'excess_heat_200_500c' | 'excess_heat_500c'

    const data: Record<HeatKey, number> = {
      excess_heat_100_200c: Number(properties.excess_heat_100_200c) || 0,
      excess_heat_200_500c: Number(properties.excess_heat_200_500c) || 0,
      excess_heat_500c: Number(properties.excess_heat_500c) || 0,
    }

    const colors: Record<HeatKey, string> = {
      excess_heat_100_200c: '#FFCC00',
      excess_heat_200_500c: '#FF6600',
      excess_heat_500c: '#FF0000',
    }

    const total = Object.values(data).reduce((sum, value) => sum + value, 0)

    if (total === 0) {
      return ''
    }

    const radius = size / 2
    const center = size / 2
    const nonZeroEntries = (Object.entries(data) as [HeatKey, number][]).filter(
      ([_, value]) => value > 0,
    )

    if (nonZeroEntries.length === 1) {
      const [key] = nonZeroEntries[0]

      return `
    <svg xmlns="http://www.w3.org/2000/svg"
         width="${size}"
         height="${size}"
         viewBox="0 0 ${size} ${size}">
      <circle
        cx="${center}"
        cy="${center}"
        r="${radius}"
        fill="${colors[key]}"
      />
    </svg>
  `
    }

    let svg = `
    <svg xmlns="http://www.w3.org/2000/svg"
         width="${size}"
         height="${size}"
         viewBox="0 0 ${size} ${size}">
  `

    let currentAngle = 0

    ;(Object.entries(data) as [HeatKey, number][]).forEach(([key, value]) => {
      if (value <= 0) {
        return
      }

      const percentage = value / total
      const angle = percentage * 360

      const startAngle = currentAngle
      const endAngle = currentAngle + angle

      const start = this._polarToCartesian(center, center, radius, endAngle)

      const end = this._polarToCartesian(center, center, radius, startAngle)

      const largeArcFlag = angle > 180 ? 1 : 0

      svg += `
      <path
        d="
          M ${center} ${center}
          L ${start.x} ${start.y}
          A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}
          Z
        "
        fill="${colors[key]}"
      />
    `

      currentAngle = endAngle
    })

    svg += `</svg>`

    return svg
  }
  private _polarToCartesian(
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number,
  ) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180

    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    }
  }
}
